import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import languageService from '../../services/language';
import { setLanguage, SHIPPED_LANGUAGES } from '../../i18n';
import { nativeName, englishName, REQUESTABLE_LANGUAGES } from '../../i18n/languages';
import { ApiError } from '../../types';

const OTHER = '__other';

/**
 * Account & Security → Language. Choosing one switches this device at once
 * and saves it on the account, so every device follows. A language not on the
 * list can be asked for, which emails support@.
 */
export default function LanguageCard() {
  const { t, i18n } = useTranslation('common', { keyPrefix: 'LanguageCard' });
  const current = i18n.language;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [asking, setAsking] = useState(false);
  const [requested, setRequested] = useState('');
  const [otherName, setOtherName] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  const requestable = useMemo(
    () => REQUESTABLE_LANGUAGES
      .filter((c) => !SHIPPED_LANGUAGES.includes(c))
      .map((c) => ({ code: c, label: `${nativeName(c)} — ${englishName(c)}` }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );

  const choose = async (code: string) => {
    setSaving(true);
    setSaveError(null);
    await setLanguage(code);
    try {
      const r = await languageService.set(code === 'en' ? null : code);
      if (!r.success) setSaveError(t('saveFailed'));
    } catch {
      setSaveError(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const ask = async () => {
    const language = requested === OTHER ? otherName.trim() : requested;
    if (!language) return;
    setSending(true);
    setRequestError(null);
    try {
      const r = await languageService.request(language, note.trim());
      if (!r.success) {
        setRequestError(r.error.message);
        return;
      }
      setSent(requested === OTHER ? language : nativeName(language));
      setAsking(false);
      setRequested('');
      setOtherName('');
      setNote('');
    } catch (err) {
      setRequestError(err instanceof ApiError ? err.message : t('requestFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Languages className="h-5 w-5 text-primary-600" />
          {t('title')}
        </CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label htmlFor="app-language" className="block text-sm font-medium text-neutral-700 mb-1">
            {t('label')}
          </label>
          <select
            id="app-language"
            value={SHIPPED_LANGUAGES.includes(current) ? current : 'en'}
            disabled={saving}
            onChange={(e) => void choose(e.target.value)}
            className="w-full sm:w-80 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
          >
            {SHIPPED_LANGUAGES.map((code) => (
              <option key={code} value={code}>
                {code === 'en' ? 'English' : nativeName(code)}
              </option>
            ))}
          </select>
          {current !== 'en' && (
            <p className="text-xs text-neutral-500 mt-1.5">{t('partial')}</p>
          )}
          {saveError && <p className="text-sm text-error-700 mt-1.5">{saveError}</p>}
        </div>

        {sent && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
            {t('sent', { language: sent })}
          </div>
        )}

        {!asking ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-sm text-neutral-600">{t('notListed')}</span>
            <Button variant="outline" size="sm" onClick={() => { setAsking(true); setSent(null); }}>
              {t('ask')}
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border border-neutral-200 p-3 space-y-3">
            <div>
              <label htmlFor="request-language" className="block text-sm font-medium text-neutral-700 mb-1">
                {t('which')}
              </label>
              <select
                id="request-language"
                value={requested}
                onChange={(e) => setRequested(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              >
                <option value="">{t('choose')}</option>
                {requestable.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
                <option value={OTHER}>{t('another')}</option>
              </select>
            </div>
            {requested === OTHER && (
              <div>
                <label htmlFor="request-other" className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('otherName')}
                </label>
                <input
                  id="request-other"
                  value={otherName}
                  maxLength={60}
                  onChange={(e) => setOtherName(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                />
              </div>
            )}
            <div>
              <label htmlFor="request-note" className="block text-sm font-medium text-neutral-700 mb-1">
                {t('note')}
              </label>
              <textarea
                id="request-note"
                value={note}
                maxLength={1000}
                rows={3}
                placeholder={t('notePlaceholder')}
                onChange={(e) => setNote(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
            </div>
            {requestError && <p className="text-sm text-error-700">{requestError}</p>}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => void ask()}
                isLoading={sending}
                disabled={!requested || (requested === OTHER && !otherName.trim())}
              >
                {t('send')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setAsking(false)}>
                {t('cancel')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
